import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FIELDS = [
  ["sender_name", "Sender name", "AetherTags Founder"],
  ["from_email", "Sending address (on your verified domain)", "founder@aethertags.com"],
  ["reply_to", "Reply-to email", "you@example.com"],
  ["signature_name", "Signature name (shown as “— Name, AetherTags”)", "Founder"],
];

const SenderStatus = ({ sender }) => (
  <div data-testid="email-sender-status" className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm">
    <p className="eyebrow">Sending domain</p>
    <p className="mt-2">
      <span className="font-mono">{sender.domain}</span> ·{" "}
      <span data-testid="email-sender-domain-status" className={sender.status === "verified" ? "text-[#5EE6D0]" : "text-[#F3DDAE]"}>
        {sender.key_configured ? (sender.status === "send_only" ? "sending key · domain accepted by Resend on send" : sender.status.replace(/_/g, " ")) : "Resend API key not added yet"}
      </span>
    </p>
    {sender.records.length > 0 && (
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs" data-testid="email-dns-records">
          <thead className="text-left text-muted-foreground"><tr>{["Type", "Name", "Value", "Priority", "Status"].map((c) => <th key={c} className="py-1 pr-3">{c}</th>)}</tr></thead>
          <tbody>{sender.records.map((r) => (
            <tr key={`${r.type}-${r.name}-${r.value}`} className="border-t border-white/5 align-top">
              <td className="py-1.5 pr-3">{r.type}</td><td className="py-1.5 pr-3 font-mono">{r.name}</td>
              <td className="max-w-[320px] break-all py-1.5 pr-3 font-mono">{r.value}</td><td className="py-1.5 pr-3">{r.priority ?? "—"}</td><td className="py-1.5">{r.status}</td>
            </tr>))}</tbody>
        </table>
      </div>
    )}
  </div>
);

export const AdminEmailSettings = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const apply = (d) => { setData(d); setForm(d.settings); };
  useEffect(() => { api.get("/admin/email-settings").then((r) => apply(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      apply((await api.put("/admin/email-settings", form)).data);
      toast.success("Invite email settings saved.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (!form) return <div className="h-64 rounded-2xl shimmer" />;
  const sendTest = async (path, label, body = {}) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(path, body);
      toast.success(`${label} sent to ${r.to}.`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div data-testid="admin-email-settings" className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <form onSubmit={save} noValidate className="glass hairline-gold space-y-4 rounded-2xl border p-5 sm:p-6">
        <p className="eyebrow">Invite email</p>
        {FIELDS.map(([k, label, ph]) => (
          <label key={k} className="block text-sm">
            <span className="text-muted-foreground">{label}</span>
            <Input value={form[k]} onChange={set(k)} placeholder={ph} data-testid={`email-settings-${k}`} className="mt-1.5 rounded-xl" />
          </label>
        ))}
        <label className="block text-sm">
          <span className="text-muted-foreground">Postal address (required by CAN-SPAM, shown in the email footer)</span>
          <Textarea value={form.postal_address} onChange={set("postal_address")} rows={3} placeholder="Street, city, postcode, country"
            data-testid="email-settings-postal_address" className="mt-1.5 rounded-xl" />
        </label>
        <Button type="submit" disabled={busy} data-testid="email-settings-save" className="btn-aurora h-11 rounded-full px-6">{busy ? "Saving…" : "Save settings"}</Button>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="outline" disabled={busy || data.blockers.length > 0} onClick={() => sendTest("/admin/email-settings/test-invite", "Test invite")} data-testid="email-settings-send-test" className="h-11 rounded-full px-6">Send test invite to reply-to</Button>
          <Button type="button" variant="outline" disabled={busy || data.blockers.length > 0} onClick={() => sendTest("/admin/email-settings/test-invite", "Personal-style test invite", { style: "personal" })} data-testid="email-settings-send-test-personal" className="h-11 rounded-full px-6">Send personal-style test invite</Button>
          <Button type="button" variant="outline" disabled={busy || data.blockers.length > 0} onClick={() => sendTest("/admin/email-settings/test-waitlist", "Test waitlist email")} data-testid="email-settings-send-test-waitlist" className="h-11 rounded-full px-6">Send test waitlist email</Button>
        </div>
      </form>
      <div className="space-y-4">
        {data.blockers.length ? (
          <ul data-testid="email-settings-blockers" className="space-y-1.5 rounded-2xl border border-[#E9C98B]/35 bg-[#E9C98B]/[0.07] p-5 text-sm text-[#F3DDAE]">
            <li className="font-medium">Invite emails are blocked until:</li>
            {data.blockers.map((b) => <li key={b} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{b}</li>)}
          </ul>
        ) : (
          <p data-testid="email-settings-ready" className="flex items-center gap-2 rounded-2xl border border-[#5EE6D0]/30 bg-[#5EE6D0]/10 p-5 text-sm text-[#5EE6D0]"><CheckCircle2 className="h-4 w-4" />Ready to send invite emails.</p>
        )}
        <SenderStatus sender={data.sender} />
      </div>
    </div>
  );
};
